import { Col, Row } from "react-grid-system"
import { memo, useCallback, useRef } from "react"
import {
  NormalFormCountrySelectionField,
  NormalFormDateField,
  NormalFormEmailField,
  NormalFormPhoneField,
  NormalFormSelectionField,
  NormalFormTextField
} from "../molecules"
import { UserGenderTypeOption } from "@/graphql/generated"
import { UserPersonalDataProps } from "@/custom-types"
import textsData from "@/textData/UserPersonalDataFormTexts.json"
import { useUserLanguage } from "@/customHooks"

type UserPersonalDataFormControlledProps = {
  onChange: (userData: UserPersonalDataProps) => void;
  readOnly?: boolean;
  defaultUserData?: UserPersonalDataProps;
}

const emptyData: UserPersonalDataProps = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  secondaryPhoneNumber: '',
  documentId: '',
  nationality: '',
  birthDate: '',
  genderOption: UserGenderTypeOption.Other
}

export const UserPersonalDataFormControlled = memo(({ onChange, readOnly, defaultUserData }: UserPersonalDataFormControlledProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const userDataRef = useRef<UserPersonalDataProps>(defaultUserData ?? emptyData);

  const genderOptions = [
    { value: UserGenderTypeOption.Male, name: 'Male' },
    { value: UserGenderTypeOption.Female, name: 'Female' },
    { value: UserGenderTypeOption.X, name: 'X' },
    { value: UserGenderTypeOption.Other, name: 'Other' }
  ]

  const handleFieldChange = useCallback((field: keyof UserPersonalDataProps, value: string | null) => {
      userDataRef.current = {
        ...userDataRef.current,
        [field]: value ?? "",
      }
      onChange(userDataRef.current)
  }, [onChange])

  return (
    <>
      <Row>
        <Col md={4}>
          <NormalFormTextField
            label={texts.firstName}
            placeholder={texts.firstNamePlaceholder}
            name="firstName"
            value={defaultUserData?.firstName}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('firstName', value)}
            required
          />
        </Col>
        <Col md={4}>
          <NormalFormTextField
            label={texts.lastName}
            placeholder={texts.lastNamePlaceholder}
            name="lastName"
            value={defaultUserData?.lastName}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('lastName', value)}
            required
          />
        </Col>
        <Col md={4}>
          <NormalFormEmailField
            label={texts.email}
            placeholder={texts.emailPlaceholder}
            name="email"
            value={defaultUserData?.email}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('email', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormPhoneField
            label={texts.phoneNumber}
            placeholder={texts.phoneNumberPlaceholder}
            name="phoneNumber"
            value={defaultUserData?.phoneNumber}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('phoneNumber', value)}
            required
          />
        </Col>
        <Col md={6}>
          <NormalFormPhoneField
            label={texts.secondaryPhoneNumber}
            placeholder={texts.secondaryPhoneNumberPlaceholder}
            name="secondaryPhoneNumber"
            value={defaultUserData?.secondaryPhoneNumber}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('secondaryPhoneNumber', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <NormalFormTextField
            label={texts.document}
            placeholder={texts.documentPlaceholder}
            name="document"
            value={defaultUserData?.documentId}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('documentId', value)}
            required
          />
        </Col>
        <Col md={3}>
          <NormalFormCountrySelectionField
            label={texts.nationality}
            noValueText="---"
            name="nationality"
            value={defaultUserData?.nationality}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('nationality', value)}
          />
        </Col>
        <Col md={3}>
          <NormalFormDateField
            label={texts.birthDate}
            placeholder={texts.birthDatePlaceholder}
            name="birthDate"
            value={defaultUserData?.birthDate}
            readOnly={readOnly}
            onChange={(value) => handleFieldChange('birthDate', value)}
            required
          />
        </Col>
        <Col md={3}>
          <NormalFormSelectionField<UserGenderTypeOption>
            label={texts.gender}
            name="gender"
            options={genderOptions}
            value={defaultUserData?.genderOption}
            onChange={(value) => handleFieldChange('genderOption', value as string)}
            required
          />
        </Col>
      </Row>
    </>
  )
});